import { useState, useCallback, useEffect, useRef } from 'react';
import { getApiUrl } from '../config';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const BADGE_POLL_INTERVAL_MS = 30_000;

export function useSocialBadges() {
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const [groupInvitationCount, setGroupInvitationCount] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setFriendRequestCount(0);
      setGroupInvitationCount(0);
      return;
    }

    try {
      const [friendsRes, groupsRes] = await Promise.all([
        fetch(getApiUrl('/api/friends/requests'), { headers: getAuthHeaders() }).catch(() => null),
        fetch(getApiUrl('/api/groups/invitations'), { headers: getAuthHeaders() }).catch(() => null),
      ]);

      if (friendsRes?.ok) {
        const data = await friendsRes.json();
        setFriendRequestCount(data.requests?.length || 0);
      }
      if (groupsRes?.ok) {
        const data = await groupsRes.json();
        setGroupInvitationCount(data.invitations?.length || 0);
      }
    } catch {
      // Silently fail — badges are non-critical
    }
  }, []);

  useEffect(() => {
    refresh();
    intervalRef.current = setInterval(refresh, BADGE_POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refresh]);

  return { friendRequestCount, groupInvitationCount, refresh };
}
